(function installCcmLivePreviewV3() {
  if (customElements.get('ccm-live-preview-v3')) return;

  const HANDOFF_KEY = 'ccm-live-preview-v3-handoff-v1';
  const DRAG_HINT_KEY = 'ccm-live-preview-v3-drag-dismissed-v1';
  const REAR_HINT_KEY = 'ccm-live-preview-v3-rear-shown-v1';
  const VIEWER_COMMAND_SCHEMA = 'ccm-storefront-viewer-command/v1';
  const STOREFRONT_RELEASE = 'ccm-live-preview-v3-20260816-clean-front-pose';
  const HANDOFF_MAX_AGE_MS = 2 * 60 * 60 * 1000;
  const MAX_RENDERER_AUTO_RETRIES = 1;
  const RENDERER_AUTO_RETRY_DELAY_MS = 450;
  const MAX_FILE_BYTES = 10 * 1024 * 1024;
  const MAX_IMAGE_PIXELS = 40000000;
  const ACCEPTED_TYPES = new Set([
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'image/heif',
  ]);

  function createUuid() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
      const random = window.crypto?.getRandomValues
        ? window.crypto.getRandomValues(new Uint8Array(1))[0] % 16
        : Math.floor(Math.random() * 16);
      const value = character === 'x' ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  }

  function safeJsonParse(value) {
    try {
      return JSON.parse(value);
    } catch (_error) {
      return null;
    }
  }

  function wait(milliseconds) {
    return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  }

  class CcmLivePreviewV3 extends HTMLElement {
    constructor() {
      super();
      this.v3Active = false;
      this.file = null;
      this.objectUrl = '';
      this.legacyPhoto = null;
      this.legacyUploadPromise = null;
      this.createPromise = null;
      this.createAbortController = null;
      this.pollTimer = null;
      this.pollStartedAt = 0;
      this.pollInFlight = false;
      this.configureTimer = null;
      this.configureInFlight = false;
      this.configureQueued = false;
      this.failureInProgress = false;
      this.lastFailure = null;
      this.fallbackReady = null;
      this.fallbackInProgress = false;
      this.browserDiagnosticsCache = null;
      this.analyticsEmitted = new Set();
      this.sessionId = '';
      this.previewToken = '';
      this.statusPath = '';
      this.configurePath = '';
      this.cartAssociationPath = '';
      this.rendererPath = '';
      this.posterPath = '';
      this.clientEventPath = '';
      this.posterFullUrl = '';
      this.posterThumbnailUrl = '';
      this.rendererSlowTimer = null;
      this.guidanceTimer = null;
      this.posterTimer = null;
      this.currentRevision = -1;
      this.inferenceStatus = '';
      this.statusValue = '';
      this.lastSentSignature = '';
      this.loadedRendererKey = '';
      this.rendererAutoRetryKey = '';
      this.rendererAutoRetryAttempts = 0;
      this.rendererAutoRetryInFlight = false;
      this.rendererFrame = null;
      this.pendingRendererFrame = null;
      this.expectedRendererOrigin = '';
      this.pendingRendererOrigin = '';
      this.currentStageKey = 'intake';
      this.currentStageIndex = 1;
      this.currentStageLabel = '';
      this.generationKind = 'initial';
      this.lastWorkingSignature = '';
      this.lastPreviewStateSignature = '';
      this.pendingLedGuidance = false;
      this.posterCaptureRevision = -1;
      this.posterUploadRevision = -1;
      this.posterUploadingRevision = -1;
      this.posterRetryRevision = -1;
      this.allowNextSubmit = false;
      this.submissionInFlight = false;
      this.associatedCartToken = '';
      this.cartEditContext = null;
      this.restoringCartEdit = false;
      this.unsubscribeVariant = null;
      this.textTimer = null;
      this.handleWindowMessage = this.handleWindowMessage.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleDocumentInput = this.handleDocumentInput.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.handleHomepageNavigation = this.handleHomepageNavigation.bind(this);
      this.handlePageHide = this.handlePageHide.bind(this);
      this.handleV2FallbackReady = this.handleV2FallbackReady.bind(this);
      this.handleV2FallbackFailed = this.handleV2FallbackFailed.bind(this);
    }

    connectedCallback() {
      if (!this.rolloutEnabled()) return;

      this.v3Active = true;
      this.hidden = false;
      this.fileInput = this.querySelector('[data-v3-file-input]');
      this.sample = this.querySelector('[data-v3-sample]');
      this.invitation = this.querySelector('[data-v3-invitation]');
      this.working = this.querySelector('[data-v3-working]');
      this.sourceImage = this.querySelector('[data-v3-source-image]');
      this.overlayHeading = this.querySelector('[data-v3-overlay-heading]');
      this.overlayBody = this.querySelector('[data-v3-overlay-body]');
      this.progress = this.querySelector('[data-v3-progress]');
      this.progressTime = this.querySelector('[data-v3-progress-time]');
      this.rendererHost = this.querySelector('[data-v3-renderer]');
      this.guidance = this.querySelector('[data-v3-guidance]');
      this.guidanceHeading = this.querySelector('[data-v3-guidance-heading]');
      this.guidanceBody = this.querySelector('[data-v3-guidance-body]');
      this.statusHeading = this.querySelector('[data-v3-status-heading]');
      this.statusBody = this.querySelector('[data-v3-status-body]');
      this.changePhotoButton = this.querySelector('[data-v3-change-photo]');
      this.retryButton = this.querySelector('[data-v3-retry]');
      this.fallbackButton = this.querySelector('[data-v3-fallback]');
      this.cartEditBanner = this.querySelector('[data-v3-cart-edit-banner]');
      this.catalogViewer = this.querySelector('ccm-native-crystal-viewer');
      this.productForm = this.dataset.productFormId
        ? document.getElementById(this.dataset.productFormId)
        : null;
      this.desiredSelection = this.readSelection();
      this.stageLabels = {
        intake: this.dataset.stageIntake,
        background_removal: this.dataset.stageBackgroundRemoval,
        depth_estimation: this.dataset.stageDepthEstimation,
        engraving_field: this.dataset.stageEngravingField,
        field_packaging: this.dataset.stageFieldPackaging,
        crystal_compilation: this.dataset.stageCrystalCompilation,
      };

      this.activateSurface();
      this.bindControls();
      this.showInitial();

      if (this.dataset.surface === 'pdp') {
        const editSessionId = this.cartEditSessionId();
        if (editSessionId) this.restoreCartEdit(editSessionId).catch(() => {});
        else this.restoreHandoff();
        window.CCMOpenProductPreview = () => this.focusViewer();
      }

      this.emitAnalytics('live_preview_v3_opened');
    }

    disconnectedCallback() {
      window.removeEventListener('message', this.handleWindowMessage);
      window.removeEventListener('pagehide', this.handlePageHide);
      document.removeEventListener('click', this.handleDocumentClick, true);
      document.removeEventListener('input', this.handleDocumentInput, true);
      document.removeEventListener('change', this.handleDocumentInput, true);
      document.removeEventListener('ccm:live-preview-v2:ready', this.handleV2FallbackReady);
      document.removeEventListener('ccm:live-preview-v2:failed', this.handleV2FallbackFailed);
      this.productForm?.removeEventListener('submit', this.handleFormSubmit, true);
      const productFormHost = this.productForm?.closest('product-form');
      if (productFormHost?.cartEditCoordinator === this) productFormHost.cartEditCoordinator = null;
      if (productFormHost?.livePreviewV3Coordinator === this) productFormHost.livePreviewV3Coordinator = null;
      this.homeProductLink?.removeEventListener('click', this.handleHomepageNavigation, true);
      this.unsubscribeVariant?.();
      this.stopPolling();
      window.clearTimeout(this.configureTimer);
      window.clearTimeout(this.textTimer);
      window.clearTimeout(this.rendererSlowTimer);
      window.clearTimeout(this.guidanceTimer);
      window.clearTimeout(this.posterTimer);
      this.createAbortController?.abort();
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    }

    get isActive() {
      return this.v3Active;
    }

    rolloutEnabled() {
      const mode = String(this.dataset.mode || 'off').toLowerCase();
      if (mode === 'on') return true;
      return mode === 'internal' && new URLSearchParams(window.location.search).get('preview_v3') === '1';
    }

    activateSurface() {
      if (this.dataset.surface === 'homepage') {
        this.closest('.theme-preview-popup')?.classList.add('is-v3-active');
      } else {
        this.closest('.product__preview')?.classList.add('is-v3-active');
        this.closest('product-info')?.querySelectorAll('.orientation-link').forEach((link) => {
          const url = new URL(link.href, window.location.origin);
          url.searchParams.set('preview_v3', '1');
          const currentUrl = new URL(window.location.href);
          [
            'preview_theme_id',
            'view',
            'edit_preview_session',
            'edit_cart_group',
            'edit_cart_key',
            'edit_focus',
          ].forEach((parameter) => {
            const value = currentUrl.searchParams.get(parameter);
            if (value) url.searchParams.set(parameter, value);
          });
          link.href = url.pathname + url.search;
        });
      }
    }

    bindControls() {
      this.querySelector('[data-v3-upload]')?.addEventListener('click', () => this.openFilePicker());
      this.querySelector('[data-v3-dismiss]')?.addEventListener('click', () => this.dismissInvitation());
      this.changePhotoButton?.addEventListener('click', () => this.openFilePicker());
      this.retryButton?.addEventListener('click', () => {
        if (this.file) this.startPreview(this.file);
        else this.openFilePicker();
      });
      this.fallbackButton?.addEventListener('click', () => this.startV2Fallback());
      this.fileInput?.addEventListener('change', () => {
        const file = this.fileInput.files?.[0];
        if (file) this.handleFile(file);
      });

      window.addEventListener('message', this.handleWindowMessage);
      window.addEventListener('pagehide', this.handlePageHide);
      document.addEventListener('click', this.handleDocumentClick, true);
      document.addEventListener('input', this.handleDocumentInput, true);
      document.addEventListener('change', this.handleDocumentInput, true);
      document.addEventListener('ccm:live-preview-v2:ready', this.handleV2FallbackReady);
      document.addEventListener('ccm:live-preview-v2:failed', this.handleV2FallbackFailed);
      this.productForm?.addEventListener('submit', this.handleFormSubmit, true);
      const productFormHost = this.productForm?.closest('product-form');
      if (productFormHost) productFormHost.livePreviewV3Coordinator = this;
      if (this.dataset.surface === 'homepage') {
        this.homeProductLink = this.closest('.theme-preview-popup')?.querySelector('[data-target="product-url"]');
        this.homeProductLink?.addEventListener('click', this.handleHomepageNavigation, true);
      }

      if (
        this.dataset.surface === 'pdp'
        && typeof subscribe === 'function'
        && typeof PUB_SUB_EVENTS !== 'undefined'
        && PUB_SUB_EVENTS.variantChange
      ) {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          const section = this.closest('product-info');
          if (section?.dataset.section && data?.sectionId && section.dataset.section !== data.sectionId) return;
          if (data?.variant?.sku) this.updateSelection({ crystal_sku: data.variant.sku });
          if (this.cartEditContext) window.setTimeout(() => this.activateCartEditMode(), 0);
        });
      }
    }

    handleDocumentClick(event) {
      if (!this.v3Active || this.dataset.surface !== 'pdp') return;

      const orientationLink = event.target.closest('.orientation-link');
      if (orientationLink) {
        const url = new URL(orientationLink.href, window.location.origin);
        url.searchParams.set('preview_v3', '1');
        const currentUrl = new URL(window.location.href);
        [
          'preview_theme_id',
          'view',
          'edit_preview_session',
          'edit_cart_group',
          'edit_cart_key',
          'edit_focus',
        ].forEach((parameter) => {
          const value = currentUrl.searchParams.get(parameter);
          if (value) url.searchParams.set(parameter, value);
        });
        orientationLink.href = url.pathname + url.search;
        this.persistHandoff();
        return;
      }

      const button = event.target.closest('.product-upload-photo .theme-button-secondary');
      if (!button) return;
      const uploadBlock = button.closest('.product-upload-photo');
      if (
        uploadBlock?.dataset.productFormId
        && this.dataset.productFormId
        && uploadBlock.dataset.productFormId !== this.dataset.productFormId
      ) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.openFilePicker();
    }

    handleDocumentInput(event) {
      if (!this.v3Active || this.dataset.surface !== 'pdp' || this.restoringCartEdit) return;
      const target = event.target;
      if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;
      const formId = target.getAttribute('form');
      if (formId && this.dataset.productFormId && formId !== this.dataset.productFormId) return;

      if (target.matches('variant-selects-extended input[data-base-sku]')) {
        this.updateSelection({ base_sku: target.dataset.baseSku || 'none' });
        return;
      }

      if (target.name === 'properties[Engrave Text]') {
        window.clearTimeout(this.textTimer);
        this.textTimer = window.setTimeout(() => {
          this.updateSelection({ engraving_text: target.value || '' });
        }, 500);
        return;
      }

      if (target.name === 'properties[Font]') {
        this.updateSelection({ engraving_font: target.value || 'Georgia' });
        return;
      }

      if (target.name === 'properties[Crystal SKU]') {
        this.updateSelection({ crystal_sku: target.value || this.dataset.initialSku || '' });
      }
    }

    controlValue(name, fallback = '') {
      const formId = this.dataset.productFormId;
      const form = formId ? document.getElementById(formId) : null;
      const associatedControl = formId
        ? document.querySelector(`[form="${CSS.escape(formId)}"][name="${CSS.escape(name)}"]`)
        : null;
      if (associatedControl) return associatedControl.value;
      const formControl = form?.querySelector(`[name="${CSS.escape(name)}"]`);
      return formControl ? formControl.value : fallback;
    }

    readSelection() {
      const scope = this.closest('product-info') || document;
      const selectedBase = scope.querySelector('variant-selects-extended input[data-base-sku]:checked');
      const selectedHomepageCrystal = this.dataset.surface === 'homepage'
        ? this.closest('.theme-preview-popup')
          ?.querySelector('.crystal-type input[type="radio"]:checked')
          ?.dataset.productSku
        : '';
      return {
        crystal_sku: this.controlValue(
          'properties[Crystal SKU]',
          selectedHomepageCrystal || this.desiredSelection?.crystal_sku || this.dataset.initialSku || '',
        ),
        orientation: this.dataset.defaultOrientation || 'tower',
        base_sku: selectedBase?.dataset.baseSku || this.dataset.initialBaseSku || 'none',
        engraving_text: this.controlValue('properties[Engrave Text]', ''),
        engraving_font: this.controlValue('properties[Font]', 'Georgia'),
      };
    }

    syncSelectionFromControls() {
      const selection = this.readSelection();
      this.desiredSelection = { ...this.desiredSelection, ...selection };
      return this.desiredSelection;
    }

    updateSelection(nextSelection) {
      const previousSignature = this.selectionSignature(this.desiredSelection);
      const previousBase = this.desiredSelection?.base_sku || 'none';
      this.desiredSelection = { ...this.desiredSelection, ...nextSelection };
      const nextSignature = this.selectionSignature(this.desiredSelection);
      if (previousSignature === nextSignature) return;

      if (nextSelection.crystal_sku && this.catalogViewer?.setSku) {
        this.catalogViewer.setSku(nextSelection.crystal_sku);
      }
      if (nextSelection.base_sku && this.catalogViewer) {
        this.catalogViewer.setAttribute('base-mode', nextSelection.base_sku);
      }
      if (
        nextSelection.base_sku
        && nextSelection.base_sku !== 'none'
        && nextSelection.base_sku !== previousBase
      ) {
        this.pendingLedGuidance = true;
      }
      if (this.sessionId) {
        this.emitAnalytics('live_preview_v3_product_changed');
        this.scheduleConfigure();
      }
      this.persistHandoff();
    }

    dismissInvitation() {
      if (!this.invitation) return;
      this.invitation.classList.add('is-dismissed');
      this.changePhotoButton.textContent = this.dataset.copyUpload;
      this.changePhotoButton.hidden = false;
    }

    handlePageHide() {
      this.persistHandoff();
    }

    setSelection(nextSelection) {
      this.updateSelection(nextSelection);
    }

    selectionSignature(selection) {
      if (!selection) return '';
      return JSON.stringify([
        selection.crystal_sku || '',
        selection.orientation || '',
        selection.base_sku || 'none',
        selection.engraving_text || '',
        selection.engraving_font || 'Georgia',
      ]);
    }

    mountExternalUploader(container) {
      if (!this.v3Active || !container) return;
      const button = document.createElement('button');
      const heading = document.createElement('strong');
      const body = document.createElement('span');
      button.type = 'button';
      button.className = 'ccm-live-preview-v3__external-upload';
      heading.textContent = this.dataset.copyUpload;
      body.textContent = this.dataset.copyInitialBody;
      button.append(heading, body);
      button.addEventListener('click', () => this.openFilePicker());
      container.replaceChildren(button);
      container.addEventListener('dragover', (event) => {
        event.preventDefault();
        container.classList.add('is-dragging');
      });
      container.addEventListener('dragleave', () => container.classList.remove('is-dragging'));
      container.addEventListener('drop', (event) => {
        event.preventDefault();
        container.classList.remove('is-dragging');
        const file = event.dataTransfer?.files?.[0];
        if (file) this.handleFile(file);
      });
    }

    openFilePicker() {
      if (!this.v3Active) {
        this.openLegacyPicker();
        return;
      }
      this.openV3Picker();
    }

    resetAndOpen() {
      if (!this.v3Active) {
        this.openLegacyPicker();
        return;
      }
      this.stopPolling();
      this.createAbortController?.abort();
      this.sessionId = '';
      this.previewToken = '';
      this.statusPath = '';
      this.configurePath = '';
      this.cartAssociationPath = '';
      this.rendererPath = '';
      this.posterPath = '';
      this.clientEventPath = '';
      this.posterFullUrl = '';
      this.posterThumbnailUrl = '';
      window.clearTimeout(this.rendererSlowTimer);
      window.clearTimeout(this.guidanceTimer);
      window.clearTimeout(this.posterTimer);
      this.currentRevision = -1;
      this.lastSentSignature = '';
      this.loadedRendererKey = '';
      this.rendererAutoRetryKey = '';
      this.rendererAutoRetryAttempts = 0;
      this.rendererAutoRetryInFlight = false;
      this.rendererFrame = null;
      this.pendingRendererFrame = null;
      this.posterCaptureRevision = -1;
      this.posterUploadRevision = -1;
      this.posterUploadingRevision = -1;
      this.posterRetryRevision = -1;
      this.lastPreviewStateSignature = '';
      this.associatedCartToken = '';
      this.rendererHost?.replaceChildren();
      this.clearLineProperties();
      this.file = null;
      this.legacyPhoto = null;
      this.legacyUploadPromise = null;
      this.fallbackReady = null;
      this.fallbackInProgress = false;
      this.lastFailure = null;
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = '';
      try {
        sessionStorage.removeItem(HANDOFF_KEY);
      } catch (_error) {
        // Storage can be unavailable in strict privacy modes.
      }
      this.showInitial();
      this.openFilePicker();
    }

    createDeferred() {
      let resolve;
      let reject;
      const promise = new Promise((resolvePromise, rejectPromise) => {
        resolve = resolvePromise;
        reject = rejectPromise;
      });
      return { promise, resolve, reject };
    }

    async openV3Picker() {
      try {
        const client = await this.waitForFilestack();
        const deferred = this.createDeferred();
        let selectedBrowserFile = null;
        let previewStarted = false;
        client.picker({
          accept: window.filestackInstance?.accept || ['image/*'],
          maxFiles: 1,
          maxSize: MAX_FILE_BYTES,
          fromSources: ['local_file_system'],
          exposeOriginalFile: true,
          lang: document.documentElement.lang?.toLowerCase().startsWith('ko') ? 'ko' : 'en',
          onFileSelected: async (pickerFile) => {
            const originalFile = pickerFile?.originalFile;
            if (!(originalFile instanceof File)) {
              throw new Error(this.dataset.copyInvalidFile);
            }
            const validationMessage = await this.validateFile(originalFile);
            if (validationMessage) throw new Error(validationMessage);
            selectedBrowserFile = originalFile;
            previewStarted = true;
            this.handleFile(originalFile, { legacyUploadPromise: deferred.promise }).catch(() => {});
          },
          onUploadDone: (result) => {
            const uploaded = result.filesUploaded?.[0];
            if (!uploaded || !selectedBrowserFile) {
              deferred.reject(new Error('Legacy photo storage is unavailable.'));
              return;
            }
            const legacy = this.rememberLegacyPhoto(uploaded, selectedBrowserFile);
            deferred.resolve(legacy);
          },
          onCancel: () => {
            if (previewStarted) deferred.reject(new Error('Photo upload was cancelled.'));
          },
          onFileUploadFailed: (_pickerFile, error) => {
            deferred.reject(error || new Error('Legacy photo storage is unavailable.'));
          },
        }).open();
      } catch (error) {
        this.showError(error?.message || this.dataset.copyErrorBody);
      }
    }

    async openLegacyPicker() {
      try {
        const client = await this.waitForFilestack();
        client.picker({
          accept: window.filestackInstance?.accept || ['image/*'],
          maxFiles: 1,
          fromSources: window.filestackInstance?.fromSources || ['local_file_system'],
          onUploadDone: (result) => {
            const file = result.filesUploaded?.[0];
            if (
              !file
              || typeof publish !== 'function'
              || typeof PUB_SUB_EVENTS === 'undefined'
              || !PUB_SUB_EVENTS.filestackFileChoiced
            ) return;
            publish(PUB_SUB_EVENTS.filestackFileChoiced, {
              data: {
                url: file.url,
                filename: file.filename,
                mimetype: file.mimetype,
                size: file.size,
                source: file.source,
                uploadId: file.uploadId,
              },
            });
          },
        }).open();
      } catch (_error) {
        this.showError(this.dataset.copyErrorBody);
      }
    }

    async handleFile(file, options = {}) {
      const validationMessage = await this.validateFile(file);
      if (validationMessage) {
        this.showError(validationMessage);
        return false;
      }

      this.file = file;
      if (!options.preserveReorderSource) this.markAccountReorderPhotoChanged();
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = URL.createObjectURL(file);
      this.clearLineProperties();
      this.legacyPhoto = null;
      this.associatedCartToken = '';
      this.legacyUploadPromise = options.legacyUploadPromise
        || this.uploadLegacyPhoto(file, options).catch(() => null);
      this.generationKind = 'initial';
      this.currentStageKey = 'intake';
      this.currentStageIndex = 1;
      this.currentStageLabel = this.stageLabels.intake || this.dataset.copyProcessingHeading;
      this.showWorking(
        this.currentStageLabel,
        this.dataset.copyProcessingBody,
        this.currentStageIndex,
        this.generationKind,
      );
      this.emitPreviewState('creating');
      this.dispatchEvent(new CustomEvent('ccm:live-preview-v3:file-selected', {
        detail: {
          thumbnailUrl: this.objectUrl,
          accountReorderHydration: options.preserveReorderSource === true,
        },
        bubbles: true,
      }));
      this.startPreview(file).catch(() => {});
      return true;
    }

    async validateFile(file) {
      const type = String(file.type || '').toLowerCase();
      const extension = String(file.name || '').split('.').pop()?.toLowerCase();
      const extensionAccepted = extension === 'heic' || extension === 'heif';
      if ((!ACCEPTED_TYPES.has(type) && !extensionAccepted) || !file.size) {
        return this.dataset.copyInvalidFile;
      }
      if (file.size > MAX_FILE_BYTES) return this.dataset.copyFileTooLarge;

      if (type === 'image/heic' || type === 'image/heif' || extensionAccepted) return '';
      if (!window.createImageBitmap) return '';
      try {
        const bitmap = await createImageBitmap(file);
        const pixels = bitmap.width * bitmap.height;
        this.fileMetrics = { width: bitmap.width, height: bitmap.height };
        bitmap.close();
        if (pixels > MAX_IMAGE_PIXELS) return this.dataset.copyFileTooLarge;
      } catch (_error) {
        return this.dataset.copyInvalidFile;
      }
      return '';
    }

    startPreview(file) {
      this.stopPolling();
      this.createAbortController?.abort();
      this.createAbortController = new AbortController();
      this.failureInProgress = false;
      [
        'live_preview_v3_upload_started',
        'live_preview_v3_session_created',
        'live_preview_v3_ready',
        'live_preview_v3_failed',
      ].forEach((eventName) => this.analyticsEmitted.delete(eventName));
      this.sessionId = createUuid();
      this.previewToken = '';
      this.statusPath = '';
      this.configurePath = '';
      this.cartAssociationPath = '';
      this.rendererPath = '';
      this.posterPath = '';
      this.clientEventPath = '';
      this.posterFullUrl = '';
      this.posterThumbnailUrl = '';
      this.currentRevision = -1;
      this.inferenceStatus = '';
      this.statusValue = '';
      this.fallbackReady = null;
      this.fallbackInProgress = false;
      this.lastFailure = null;
      this.syncSelectionFromControls();
      this.lastSentSignature = this.selectionSignature(this.desiredSelection);
      this.loadedRendererKey = '';
      this.rendererAutoRetryKey = '';
      this.rendererAutoRetryAttempts = 0;
      this.rendererAutoRetryInFlight = false;
      this.posterCaptureRevision = -1;
      this.posterUploadRevision = -1;
      this.posterUploadingRevision = -1;
      this.posterRetryRevision = -1;
      this.pollStartedAt = Date.now();
      this.emitAnalytics('live_preview_v3_upload_started');

      const body = new FormData();
      body.append('image', file, file.name || 'upload');
      body.append('client_session_id', this.sessionId);
      body.append('crystal_sku', this.desiredSelection.crystal_sku);
      body.append('orientation', this.desiredSelection.orientation || 'tower');
      body.append('base_sku', this.desiredSelection.base_sku || 'none');
      body.append('engraving_text', this.desiredSelection.engraving_text || '');
      body.append('engraving_font', this.desiredSelection.engraving_font || 'Georgia');

      this.createPromise = this.requestJson(this.dataset.createPath, {
        method: 'POST',
        body,
        signal: this.createAbortController.signal,
        diagnosticBoundary: 'create',
      })
        .then((payload) => {
          this.applyResponse(payload);
          this.emitAnalytics('live_preview_v3_session_created');
          this.persistHandoff();
          if (payload.status !== 'ready') this.schedulePoll();
          return payload;
        })
        .catch((error) => {
          if (error.name === 'AbortError') return null;
          this.handlePreviewFailure(error, { phase: 'create' });
          throw error;
        });

      return this.createPromise;
    }

    async requestJson(path, options = {}) {
      const {
        diagnosticBoundary = 'api_request',
        retryTransient = true,
        ...fetchOptions
      } = options;
      const requestHeaders = {
        Accept: 'application/json',
        ...(fetchOptions.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...(fetchOptions.headers || {}),
      };
      const attempts = retryTransient ? 2 : 1;
      let lastError = null;
      for (let attempt = 1; attempt <= attempts; attempt += 1) {
        try {
          const response = await fetch(path, {
            credentials: 'same-origin',
            cache: 'no-store',
            ...fetchOptions,
            headers: requestHeaders,
          });
          const payload = safeJsonParse(await response.text()) || {};
          if (!response.ok) {
            const error = new Error(payload.message || this.dataset.copyErrorBody);
            error.status = response.status;
            error.code = payload.code || '';
            throw error;
          }
          return payload;
        } catch (error) {
          lastError = error;
          if (error?.name === 'AbortError' || attempt >= attempts || !this.isTransientError(error)) {
            throw error;
          }
          this.emitAnalytics('live_preview_v3_retry_started');
          this.reportClientEvent('retry_started', error, {
            phase: diagnosticBoundary,
            severity: 'warning',
            retryable: true,
          });
          await wait(550);
        }
      }
      throw lastError;
    }

    isTransientError(error) {
      const status = Number(error?.status || 0);
      return error?.name === 'TypeError'
        || status === 408
        || status === 425
        || status === 429
        || status >= 500;
    }

    applyResponse(payload) {
      const revision = Number(payload.compilation_revision);
      if (Number.isFinite(revision) && revision < this.currentRevision) return false;
      if (Number.isFinite(revision)) {
        if (this.currentRevision >= 0 && revision > this.currentRevision) {
          this.invalidatePoster();
        }
        this.currentRevision = revision;
      }

      this.sessionId = payload.session_id || payload.id || this.sessionId;
      this.previewToken = payload.preview_token || this.previewToken;
      this.statusPath = payload.status_path || this.statusPath;
      this.configurePath = payload.configure_path || this.configurePath;
      this.cartAssociationPath = payload.cart_association_path || this.cartAssociationPath;
      this.rendererPath = payload.renderer_path || this.rendererPath;
      this.posterPath = payload.poster_path || this.posterPath;
      this.clientEventPath = payload.client_event_path || this.clientEventPath;
      this.statusValue = payload.status || this.statusValue;
      this.inferenceStatus = payload.inference_status || this.inferenceStatus;
      if (payload.poster) {
        this.applyPoster(payload.poster);
        if (this.posterFullUrl && this.currentRevision >= 0) {
          this.posterUploadRevision = this.currentRevision;
        }
      }

      if (payload.status === 'failed') {
        throw new Error(payload.error?.message || payload.message || this.dataset.copyErrorBody);
      }

      if (payload.status === 'ready') {
        if (this.selectionSignature(this.desiredSelection) !== this.lastSentSignature) {
          this.scheduleConfigure(0);
        } else {
          this.loadRenderer();
        }
      } else {
        this.showApiStage(payload.stages || []);
        if (this.inferenceStatus === 'ready') this.scheduleConfigure();
      }

      this.persistHandoff();
      return true;
    }

    showApiStage(stages) {
      const active = stages.find((stage) => stage?.status === 'active');
      const nextPending = stages.find((stage) => stage?.status === 'pending');
      const latestComplete = [...stages].reverse().find((stage) => stage?.status === 'complete');
      const phaseByStage = {
        intake: 1,
        background_removal: 1,
        depth_estimation: 2,
        engraving_field: 2,
        field_packaging: 3,
        crystal_compilation: 4,
      };
      const stageKey = active?.key
        || nextPending?.key
        || latestComplete?.key
        || this.currentStageKey
        || 'intake';
      const reportedPhase = phaseByStage[stageKey] || 1;
      const phase = Math.max(this.currentStageIndex || 1, reportedPhase);
      const heading = this.stageLabels[stageKey] || this.dataset.copyProcessingHeading;
      const elapsed = this.pollStartedAt ? Date.now() - this.pollStartedAt : 0;
      const body = elapsed >= 60000 ? this.dataset.copySlowBody : this.dataset.copyProcessingBody;
      this.currentStageKey = stageKey;
      this.currentStageIndex = phase;
      this.currentStageLabel = heading;
      this.showWorking(heading, body, phase, this.generationKind);
      this.emitPreviewState(elapsed >= 60000 ? 'slow' : 'creating');
    }

    schedulePoll() {
      if (!this.v3Active || !this.statusPath || this.statusValue === 'ready' || this.statusValue === 'failed') return;
      window.clearTimeout(this.pollTimer);
      const elapsed = this.pollStartedAt ? Date.now() - this.pollStartedAt : 0;
      this.pollTimer = window.setTimeout(() => this.poll(), elapsed >= 60000 ? 2000 : 1000);
    }

    async poll() {
      if (this.pollInFlight || !this.statusPath || !this.previewToken) return;
      this.pollInFlight = true;
      try {
        const payload = await this.requestJson(this.statusPath, {
          method: 'POST',
          body: JSON.stringify({ preview_token: this.previewToken }),
          diagnosticBoundary: 'poll',
        });
        this.applyResponse(payload);
        if (payload.status !== 'ready' && payload.status !== 'failed') this.schedulePoll();
      } catch (error) {
        this.handlePreviewFailure(error, { phase: 'poll' });
      } finally {
        this.pollInFlight = false;
      }
    }

    stopPolling() {
      window.clearTimeout(this.pollTimer);
      this.pollTimer = null;
      this.pollInFlight = false;
    }

    scheduleConfigure(delay = 500) {
      if (!this.v3Active || this.failureInProgress || !this.sessionId) return;
      window.clearTimeout(this.configureTimer);
      this.configureTimer = window.setTimeout(() => this.configure(), delay);
    }

    async configure() {
      if (!this.v3Active || this.failureInProgress || !this.configurePath || !this.previewToken) return;
      if (this.configureInFlight) {
        this.configureQueued = true;
        return;
      }
      if (this.inferenceStatus !== 'ready' && this.statusValue !== 'ready' && this.statusValue !== 'compiling') {
        return;
      }

      this.syncSelectionFromControls();
      const signature = this.selectionSignature(this.desiredSelection);
      if (signature === this.lastSentSignature) return;

      this.configureInFlight = true;
      this.configureQueued = false;
      this.generationKind = 'update';
      this.currentStageKey = 'crystal_compilation';
      this.currentStageIndex = 4;
      this.currentStageLabel = this.dataset.copyUpdatingHeading;
      this.showWorking(
        this.dataset.copyUpdatingHeading,
        this.dataset.copyProcessingBody,
        4,
        this.generationKind,
      );
      try {
        const payload = await this.requestJson(this.configurePath, {
          method: 'PATCH',
          diagnosticBoundary: 'configure',
          body: JSON.stringify({
            preview_token: this.previewToken,
            crystal_sku: this.desiredSelection.crystal_sku,
            orientation: this.desiredSelection.orientation || 'tower',
            base_sku: this.desiredSelection.base_sku || 'none',
            engraving_text: this.desiredSelection.engraving_text || '',
            engraving_font: this.desiredSelection.engraving_font || 'Georgia',
          }),
        });
        this.lastSentSignature = signature;
        this.pollStartedAt = Date.now();
        this.applyResponse(payload);
        this.schedulePoll();
      } catch (error) {
        if (error.status === 409 && this.inferenceStatus !== 'ready') {
          this.configureQueued = true;
        } else {
          this.handlePreviewFailure(error, { phase: 'configure' });
        }
      } finally {
        this.configureInFlight = false;
        if (
          this.v3Active
          && !this.failureInProgress
          && (
            this.configureQueued
            || this.selectionSignature(this.desiredSelection) !== this.lastSentSignature
          )
        ) {
          this.scheduleConfigure();
        }
      }
    }

    loadRenderer() {
      if (!this.rendererPath) return;
      const rendererKey = `${this.rendererPath}|${this.currentRevision}`;
      if (rendererKey === this.loadedRendererKey) return;
      if (rendererKey !== this.rendererAutoRetryKey) {
        this.rendererAutoRetryKey = rendererKey;
        this.rendererAutoRetryAttempts = 0;
        this.rendererAutoRetryInFlight = false;
      }
      this.loadedRendererKey = rendererKey;

      const frame = document.createElement('iframe');
      frame.title = this.dataset.copyReadyHeading;
      frame.allow = 'fullscreen';
      frame.loading = 'eager';
      frame.referrerPolicy = 'no-referrer';
      frame.src = this.rendererPath;
      frame.className = 'is-v3-staging';
      frame.addEventListener('error', () => {
        if (frame === this.pendingRendererFrame) {
          frame.remove();
          this.pendingRendererFrame = null;
          this.handlePreviewFailure(
            new Error('The 3D viewer iframe failed to load.'),
            { phase: 'renderer_load', preserveViewer: Boolean(this.rendererFrame) },
          );
        }
      }, { once: true });
      this.pendingRendererFrame?.remove();
      this.pendingRendererFrame = frame;
      this.pendingRendererOrigin = new URL(this.rendererPath, window.location.origin).origin;
      this.rendererHost.append(frame);
      this.rendererHost.hidden = false;
      this.showWorking(
        this.generationKind === 'update'
          ? this.dataset.copyUpdatingHeading
          : (this.currentStageLabel || this.dataset.copyProcessingHeading),
        this.dataset.copyProcessingBody,
        4,
        this.generationKind,
      );
      window.clearTimeout(this.rendererSlowTimer);
      this.rendererSlowTimer = window.setTimeout(() => {
        if (this.dataset.state !== 'ready') {
          this.showWorking(
            this.currentStageLabel || this.dataset.copyProcessingHeading,
            this.dataset.copySlowBody,
            4,
            this.generationKind,
          );
          this.emitPreviewState('slow');
        }
      }, 60000);
    }

    handleWindowMessage(event) {
      const fromPending = (
        this.pendingRendererFrame
        && event.source === this.pendingRendererFrame.contentWindow
        && event.origin === this.pendingRendererOrigin
      );
      const fromCurrent = (
        this.rendererFrame
        && event.source === this.rendererFrame.contentWindow
        && event.origin === this.expectedRendererOrigin
      );
      if (!fromPending && !fromCurrent) return;
      const payload = event.data || {};
      if (payload.type === 'ccm:crystal-viewer:ready') {
        this.activateRenderer(fromPending);
      } else if (payload.type === 'ccm:crystal-viewer:bridge-ready') {
        if (fromPending && this.dataset.state !== 'ready') {
          this.activateRenderer(true);
        } else {
          this.fitRendererSelection();
          this.startViewerIntroduction();
        }
      } else if (payload.type === 'ccm:crystal-viewer:interaction-start') {
        try {
          localStorage.setItem(DRAG_HINT_KEY, '1');
        } catch (_error) {
          // Guidance remains non-blocking when storage is unavailable.
        }
        this.hideGuidance();
      } else if (payload.type === 'ccm:crystal-viewer:rear-reached') {
        let shown = false;
        try {
          shown = localStorage.getItem(REAR_HINT_KEY) === '1';
          if (!shown) localStorage.setItem(REAR_HINT_KEY, '1');
        } catch (_error) {
          shown = false;
        }
        if (!shown) {
          this.showGuidance(this.dataset.copyRearHeading, this.dataset.copyRearBody, 7500);
        }
      } else if (payload.type === 'ccm:crystal-viewer:intro-complete') {
        let dismissed = false;
        try {
          dismissed = localStorage.getItem(DRAG_HINT_KEY) === '1';
        } catch (_error) {
          dismissed = false;
        }
        if (!dismissed) {
          this.showGuidance(this.dataset.copyDragHeading, this.dataset.copyDragBody, 6000);
        }
        this.schedulePosterCapture();
      } else if (payload.type === 'ccm:crystal-viewer:poster-ready') {
        this.uploadPoster(payload).catch(() => {});
      } else if (payload.type === 'ccm:crystal-viewer:poster-error') {
        if (Number(payload.revision) === this.currentRevision) {
          this.emitPosterFailure(
            'renderer',
            Number(payload.revision),
            0,
            payload.message || 'Renderer poster capture failed.',
          );
          this.posterCaptureRevision = -1;
          if (!this.posterRetryRevision || this.posterRetryRevision !== this.currentRevision) {
            this.posterRetryRevision = this.currentRevision;
            this.schedulePosterCapture(1200);
          }
        }
      } else if (payload.type === 'ccm:crystal-viewer:error') {
        const error = new Error(String(payload.message || this.dataset.copyErrorBody));
        error.name = String(payload.errorType || payload.name || 'CrystalViewerError').slice(0, 160);
        error.code = String(payload.code || '').slice(0, 160);
        this.handlePreviewFailure(error, {
          phase: String(payload.phase || 'renderer').slice(0, 64),
          preserveViewer: Boolean(fromPending && this.rendererFrame),
        });
      }
    }

    activateRenderer(fromPending) {
      const readyFrame = fromPending ? this.pendingRendererFrame : this.rendererFrame;
      if (!readyFrame) return;
      window.clearTimeout(this.rendererSlowTimer);
      if (fromPending) {
        const previousFrame = this.rendererFrame;
        this.rendererFrame = readyFrame;
        this.expectedRendererOrigin = this.pendingRendererOrigin;
        this.pendingRendererFrame = null;
        this.pendingRendererOrigin = '';
        readyFrame.classList.remove('is-v3-staging');
        readyFrame.classList.add('is-v3-active');
        if (previousFrame && previousFrame !== readyFrame) {
          previousFrame.classList.remove('is-v3-active');
          previousFrame.classList.add('is-v3-retiring');
          window.setTimeout(() => previousFrame.remove(), 450);
        }
      }
      this.sample.hidden = true;
      this.working.hidden = true;
      this.rendererHost.hidden = false;
      this.dataset.state = 'ready';
      this.statusHeading.textContent = this.dataset.copyReadyHeading;
      this.statusBody.textContent = this.dataset.copyReadyBody;
      this.changePhotoButton.textContent = this.dataset.copyChangePhoto;
      this.changePhotoButton.hidden = false;
      this.retryButton.hidden = true;
      if (this.fallbackButton) this.fallbackButton.hidden = true;
      this.emitPreviewState('ready');
      this.emitAnalytics('live_preview_v3_ready');
      if (this.rendererAutoRetryInFlight) {
        this.reportClientEvent('viewer_auto_retry_recovered', null, {
          phase: 'renderer_retry',
          severity: 'info',
          retryable: true,
        });
        this.rendererAutoRetryInFlight = false;
      }
      this.persistHandoff();
      this.fitRendererSelection();
      this.startViewerIntroduction();
      if (this.pendingLedGuidance) {
        this.pendingLedGuidance = false;
        let dragDismissed = false;
        try {
          dragDismissed = localStorage.getItem(DRAG_HINT_KEY) === '1';
        } catch (_error) {
          dragDismissed = false;
        }
        window.setTimeout(() => {
          this.showGuidance(this.dataset.copyLedHeading, this.dataset.copyLedBody, 6500);
        }, dragDismissed ? 900 : 6800);
      }
    }

    fitRendererSelection() {
      this.postViewerCommand('ccm:crystal-viewer:fit-selection', {
        crystalSku: this.desiredSelection?.crystal_sku || '',
        baseSku: this.desiredSelection?.base_sku || 'none',
      });
    }

    postViewerCommand(type, detail = {}) {
      if (!this.rendererFrame?.contentWindow) return;
      this.rendererFrame.contentWindow.postMessage(
        { schema: VIEWER_COMMAND_SCHEMA, type, ...detail },
        this.expectedRendererOrigin,
      );
    }

    startViewerIntroduction() {
      window.clearTimeout(this.posterTimer);
      let dismissed = false;
      try {
        dismissed = localStorage.getItem(DRAG_HINT_KEY) === '1';
      } catch (_error) {
        dismissed = false;
      }
      if (dismissed) {
        this.schedulePosterCapture(450);
        return;
      }
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.showGuidance(this.dataset.copyDragHeading, this.dataset.copyDragBody, 9000);
        this.schedulePosterCapture(450);
        return;
      }
      this.postViewerCommand('ccm:crystal-viewer:intro-orbit');
      this.posterTimer = window.setTimeout(() => this.schedulePosterCapture(0), 3200);
    }

    showGuidance(heading, body, duration = 7000) {
      if (!this.guidance || !heading) return;
      window.clearTimeout(this.guidanceTimer);
      this.guidanceHeading.textContent = heading;
      this.guidanceBody.textContent = body || '';
      this.guidance.hidden = false;
      this.guidanceTimer = window.setTimeout(() => this.hideGuidance(), duration);
    }

    hideGuidance() {
      window.clearTimeout(this.guidanceTimer);
      if (this.guidance) this.guidance.hidden = true;
    }

    schedulePosterCapture(delay = 500) {
      if (
        !this.rendererFrame
        || !this.posterPath
        || this.currentRevision < 0
        || this.posterCaptureRevision === this.currentRevision
      ) return;
      window.clearTimeout(this.posterTimer);
      this.posterTimer = window.setTimeout(() => {
        this.posterCaptureRevision = this.currentRevision;
        this.postViewerCommand('ccm:crystal-viewer:capture-poster', {
          revision: this.currentRevision,
        });
      }, delay);
    }

    async dataUrlToBlob(dataUrl) {
      const response = await fetch(dataUrl);
      if (!response.ok) throw new Error('Preview image conversion failed.');
      return response.blob();
    }

    async normalizedPosterBlob(dataUrl, width, height) {
      const sourceBlob = await this.dataUrlToBlob(dataUrl);
      let source;
      let sourceWidth;
      let sourceHeight;
      if (window.createImageBitmap) {
        source = await createImageBitmap(sourceBlob);
        sourceWidth = source.width;
        sourceHeight = source.height;
      } else {
        const objectUrl = URL.createObjectURL(sourceBlob);
        try {
          source = await new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = reject;
            image.src = objectUrl;
          });
          sourceWidth = source.naturalWidth;
          sourceHeight = source.naturalHeight;
        } finally {
          URL.revokeObjectURL(objectUrl);
        }
      }
      if (!sourceWidth || !sourceHeight) throw new Error('Preview image has invalid dimensions.');

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d', { alpha: false });
      if (!context) throw new Error('Preview image conversion is unavailable.');
      context.fillStyle = '#171a1d';
      context.fillRect(0, 0, width, height);
      const scale = Math.max(width / sourceWidth, height / sourceHeight);
      const drawWidth = sourceWidth * scale;
      const drawHeight = sourceHeight * scale;
      context.drawImage(
        source,
        (width - drawWidth) / 2,
        (height - drawHeight) / 2,
        drawWidth,
        drawHeight,
      );
      source.close?.();

      const encode = (type, quality) => new Promise((resolve) => {
        canvas.toBlob(resolve, type, quality);
      });
      return (await encode('image/webp', 0.86)) || (await encode('image/jpeg', 0.9));
    }

    emitPosterFailure(reason, revision, status = 0, message = '') {
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'live_preview_v3_poster_failed',
          live_preview_store: this.dataset.store || 'ccm',
          live_preview_version: 'v3',
          live_preview_session_id: this.sessionId || '',
          live_preview_revision: revision,
          live_preview_failure_reason: reason,
          live_preview_http_status: Number(status) || 0,
        });
      }
      const error = new Error(message || `Preview poster ${reason} failed.`);
      error.status = Number(status) || 0;
      this.reportClientEvent('poster_failed', error, {
        phase: `poster_${reason}`,
        severity: 'warning',
        retryable: true,
      });
    }

    async uploadPoster(payload) {
      const revision = Number(payload.revision);
      if (
        !this.posterPath
        || !this.previewToken
        || !Number.isFinite(revision)
        || revision !== this.currentRevision
        || revision === this.posterUploadRevision
        || revision === this.posterUploadingRevision
        || !payload.posterDataUrl
        || !payload.thumbnailDataUrl
      ) return;
      this.posterUploadingRevision = revision;
      try {
        const [poster, thumbnail] = await Promise.all([
          this.normalizedPosterBlob(payload.posterDataUrl, 960, 720),
          this.normalizedPosterBlob(payload.thumbnailDataUrl, 480, 480),
        ]);
        if (!poster || !thumbnail) throw new Error('Preview image conversion failed.');
        const body = new FormData();
        body.append('preview_token', this.previewToken);
        body.append('revision', String(revision));
        body.append('poster', poster, `crystal-preview-${revision}.webp`);
        body.append('thumbnail', thumbnail, `crystal-preview-${revision}-square.webp`);
        const response = await this.requestJson(this.posterPath, {
          method: 'POST',
          body,
          diagnosticBoundary: 'poster_upload',
        });
        if (response.poster) {
          this.applyPoster(response.poster);
          this.posterUploadRevision = revision;
        }
        this.persistHandoff();
        this.emitPreviewState('ready');
      } catch (error) {
        this.posterUploadRevision = -1;
        this.posterCaptureRevision = -1;
        this.emitPosterFailure('upload', revision, error.status, error.message);
        if (error.status === 409 && revision !== this.currentRevision) return;
        if (this.posterRetryRevision !== revision) {
          this.posterRetryRevision = revision;
          this.schedulePosterCapture(1200);
        }
      } finally {
        this.posterUploadingRevision = -1;
      }
    }

    invalidatePoster() {
      this.posterFullUrl = '';
      this.posterThumbnailUrl = '';
      this.posterCaptureRevision = -1;
      this.posterUploadRevision = -1;
      this.posterUploadingRevision = -1;
      this.posterRetryRevision = -1;
      const form = this.productForm;
      form?.querySelector('[name="properties[_preview_image_url]"]')?.remove();
      form?.querySelector('[name="properties[_preview_image_thumbnail_url]"]')?.remove();
      if (this.legacyPhoto?.url) {
        this.setLineProperty('_preview_image_url', this.legacyPhoto.url, true);
        this.setLineProperty('_preview_image_thumbnail_url', this.legacyPhoto.url, true);
      }
    }

    applyPoster(poster) {
      const full = poster.full?.url || poster.poster_url || '';
      const thumbnail = poster.thumbnail?.url || poster.thumbnail_url || full;
      if (!full) return;
      this.posterFullUrl = full;
      this.posterThumbnailUrl = thumbnail;
      this.setLineProperty('_preview_image_url', full, true);
      this.setLineProperty('_preview_image_thumbnail_url', thumbnail, true);
    }

    showInitial() {
      this.dataset.state = 'initial';
      this.sample.hidden = false;
      this.working.hidden = true;
      this.rendererHost.hidden = true;
      this.statusHeading.textContent = this.dataset.copyInitialHeading;
      this.statusBody.textContent = '';
      this.changePhotoButton.textContent = this.dataset.copyChangePhoto;
      this.changePhotoButton.hidden = true;
      this.retryButton.hidden = true;
      if (this.fallbackButton) {
        this.fallbackButton.hidden = true;
        this.fallbackButton.disabled = false;
        this.fallbackButton.textContent = this.dataset.copyFallback;
      }
    }

    showWorking(heading, body, phase = 1, generationKind = 'initial') {
      this.dataset.state = 'processing';
      this.generationKind = generationKind;
      this.currentStageIndex = Math.max(1, Math.min(4, Number(phase) || 1));
      this.currentStageLabel = heading;
      if (!this.rendererFrame) this.sample.hidden = false;
      this.working.hidden = false;
      this.rendererHost.hidden = !this.rendererFrame && !this.pendingRendererFrame;
      if (this.sourceImage && this.objectUrl) this.sourceImage.src = this.objectUrl;
      if (this.overlayHeading) {
        this.overlayHeading.textContent = generationKind === 'update'
          ? this.dataset.copyUpdatingHeading
          : this.dataset.copyProcessingHeading;
      }
      if (this.overlayBody) this.overlayBody.textContent = heading;
      if (this.progress) {
        this.progress.dataset.step = String(this.currentStageIndex);
        this.progress.setAttribute('aria-valuenow', String(this.currentStageIndex));
      }
      if (this.progressTime) this.progressTime.textContent = this.dataset.copyProcessingTime;
      this.statusHeading.textContent = heading;
      this.statusBody.textContent = '';
      this.changePhotoButton.textContent = this.dataset.copyChangePhoto;
      this.changePhotoButton.hidden = false;
      this.retryButton.hidden = true;
      if (this.fallbackButton) {
        this.fallbackButton.hidden = true;
        this.fallbackButton.disabled = false;
        this.fallbackButton.textContent = this.dataset.copyFallback;
      }
      const signature = `${heading}|${body}|${this.currentStageIndex}`;
      if (signature !== this.lastWorkingSignature) {
        this.lastWorkingSignature = signature;
        this.emitPreviewState(Date.now() - this.pollStartedAt >= 60000 ? 'slow' : 'creating');
      }
    }

    showError(message, preserveViewer = false) {
      this.dataset.state = 'error';
      this.working.hidden = true;
      if (!preserveViewer) {
        this.sample.hidden = false;
        this.rendererHost.hidden = true;
      }
      this.statusHeading.textContent = this.dataset.copyErrorHeading;
      this.statusBody.textContent = message || this.dataset.copyErrorBody;
      this.changePhotoButton.hidden = true;
      this.retryButton.hidden = false;
      if (this.fallbackButton) this.fallbackButton.hidden = !this.fallbackReady;
      this.emitPreviewState('error');
    }

    handlePreviewFailure(error, options = {}) {
      if (this.failureInProgress) return;
      const phase = options.phase || this.currentStageKey || 'unknown';
      if (this.retryRendererOnce(error, phase, options)) return;
      if (this.rendererAutoRetryInFlight) {
        this.reportClientEvent('viewer_auto_retry_failed', error, {
          phase: 'renderer_retry',
          severity: 'warning',
          retryable: false,
        });
        this.rendererAutoRetryInFlight = false;
      }
      this.failureInProgress = true;
      this.stopPolling();
      window.clearTimeout(this.configureTimer);
      this.configureQueued = false;
      window.clearTimeout(this.rendererSlowTimer);
      this.emitAnalytics('live_preview_v3_failed');
      this.lastFailure = error || new Error(this.dataset.copyErrorBody);
      this.reportClientEvent('preview_failed', this.lastFailure, {
        phase,
        severity: 'error',
        retryable: this.isTransientError(this.lastFailure),
      });
      this.prepareV2Fallback();
      const reference = this.sessionId ? ` Reference ${this.sessionId.slice(0, 8)}.` : '';
      this.showError(`${this.dataset.copyErrorBody}${reference}`, Boolean(options.preserveViewer));
    }

    retryRendererOnce(error, phase, options = {}) {
      const signature = [
        phase,
        error?.name,
        error?.code,
        error?.message,
      ].filter(Boolean).join(' ').toLowerCase();
      const rendererFailure = phase.includes('renderer')
        || phase.includes('viewer')
        || signature.includes('webgl')
        || signature.includes('context lost')
        || signature.includes('did not settle')
        || signature.includes('iframe failed to load');
      const retryKey = `${this.rendererPath}|${this.currentRevision}`;
      if (
        !rendererFailure
        || !this.rendererPath
        || !this.sessionId
        || retryKey !== this.rendererAutoRetryKey
        || this.rendererAutoRetryAttempts >= MAX_RENDERER_AUTO_RETRIES
      ) return false;

      this.rendererAutoRetryAttempts += 1;
      this.rendererAutoRetryInFlight = true;
      this.lastFailure = error || new Error(this.dataset.copyErrorBody);
      window.clearTimeout(this.rendererSlowTimer);
      window.clearTimeout(this.guidanceTimer);
      this.reportClientEvent('viewer_auto_retry_started', this.lastFailure, {
        phase,
        severity: 'warning',
        retryable: true,
      });

      const preserveViewer = Boolean(options.preserveViewer && this.rendererFrame);
      this.pendingRendererFrame?.remove();
      this.pendingRendererFrame = null;
      this.pendingRendererOrigin = '';
      if (!preserveViewer) {
        this.rendererFrame?.remove();
        this.rendererFrame = null;
        this.expectedRendererOrigin = '';
      }
      this.loadedRendererKey = '';
      this.showWorking(
        this.currentStageLabel || this.dataset.copyProcessingHeading,
        this.dataset.copySlowBody,
        4,
        this.generationKind,
      );
      window.setTimeout(() => {
        if (
          this.v3Active
          && !this.failureInProgress
          && retryKey === this.rendererAutoRetryKey
        ) {
          this.loadRenderer();
        }
      }, RENDERER_AUTO_RETRY_DELAY_MS);
      return true;
    }

    prepareV2Fallback() {
      if (!this.legacyUploadPromise) return;
      Promise.resolve(this.legacyUploadPromise)
        .then((legacy) => {
          if (!legacy?.url) return;
          this.fallbackReady = legacy;
          if (this.dataset.state === 'error' && this.fallbackButton) {
            this.fallbackButton.hidden = false;
          }
        })
        .catch(() => {});
    }

    async startV2Fallback() {
      if (this.fallbackInProgress) return;
      const legacy = this.fallbackReady || await Promise.resolve(this.legacyUploadPromise).catch(() => null);
      if (!legacy?.url) return;
      this.fallbackInProgress = true;
      if (this.fallbackButton) {
        this.fallbackButton.disabled = true;
        this.fallbackButton.textContent = 'Opening standard preview…';
      }
      this.reportClientEvent('fallback_started', this.lastFailure, {
        phase: 'v2_fallback',
        severity: 'warning',
      });
      this.emitAnalytics('live_preview_v3_fallback_started');
      this.activateV2Fallback(legacy);
    }

    handleV2FallbackReady(event) {
      if (!this.fallbackInProgress) return;
      if (event.detail?.formId && event.detail.formId !== this.dataset.productFormId) return;
      this.reportClientEvent('fallback_recovered', null, {
        phase: 'v2_fallback',
        severity: 'info',
      });
      this.emitAnalytics('live_preview_v3_fallback_recovered');
      this.fallbackInProgress = false;
    }

    handleV2FallbackFailed(event) {
      if (!this.fallbackInProgress) return;
      if (event.detail?.formId && event.detail.formId !== this.dataset.productFormId) return;
      const error = new Error(event.detail?.message || 'The standard preview also failed.');
      this.reportClientEvent('fallback_failed', error, {
        phase: 'v2_fallback',
        severity: 'error',
      });
      this.fallbackInProgress = false;
    }

    browserDiagnostics() {
      if (!this.browserDiagnosticsCache) {
        let webgl2Available = false;
        let webglContextLost = false;
        let webglVendor = '';
        let webglRenderer = '';
        try {
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('webgl2', {
            antialias: false,
            depth: false,
            stencil: false,
          });
          webgl2Available = Boolean(context);
          webglContextLost = Boolean(context?.isContextLost?.());
          const debug = context?.getExtension('WEBGL_debug_renderer_info');
          if (context && debug) {
            webglVendor = String(context.getParameter(debug.UNMASKED_VENDOR_WEBGL) || '').slice(0, 160);
            webglRenderer = String(context.getParameter(debug.UNMASKED_RENDERER_WEBGL) || '').slice(0, 160);
          }
          context?.getExtension('WEBGL_lose_context')?.loseContext();
        } catch (_error) {
          webgl2Available = false;
        }
        this.browserDiagnosticsCache = {
          user_agent: String(navigator.userAgent || '').slice(0, 500),
          browser_language: String(navigator.language || '').slice(0, 40),
          viewport_width: window.innerWidth,
          viewport_height: window.innerHeight,
          device_pixel_ratio: Number(window.devicePixelRatio || 1),
          device_memory_gb: Number(navigator.deviceMemory || 0),
          hardware_concurrency: Number(navigator.hardwareConcurrency || 0),
          connection_effective_type: String(navigator.connection?.effectiveType || '').slice(0, 20),
          connection_save_data: Boolean(navigator.connection?.saveData),
          webgl2_available: webgl2Available,
          webgl_context_lost: webglContextLost,
          webgl_vendor: webglVendor,
          webgl_renderer: webglRenderer,
          page_path: window.location.pathname,
        };
      }
      return {
        ...this.browserDiagnosticsCache,
        stage_key: this.currentStageKey || '',
        stage_index: Number(this.currentStageIndex || 0),
        file_bytes: Number(this.file?.size || 0),
        file_media_type: String(this.file?.type || '').slice(0, 100),
        file_width: Number(this.fileMetrics?.width || 0),
        file_height: Number(this.fileMetrics?.height || 0),
      };
    }

    reportClientEvent(eventName, error = null, options = {}) {
      if (!this.clientEventPath || !this.previewToken || !this.sessionId) return Promise.resolve(false);
      const status = Number(error?.status || 0);
      const payload = {
        preview_token: this.previewToken,
        event_name: eventName,
        severity: options.severity || 'error',
        phase: options.phase || this.currentStageKey || 'unknown',
        error_type: String(error?.code || error?.name || '').slice(0, 160),
        message: String(error?.message || '').slice(0, 2000),
        http_status: status >= 100 && status <= 599 ? status : null,
        retryable: Boolean(options.retryable),
        surface: this.dataset.surface || 'pdp',
        release: STOREFRONT_RELEASE,
        diagnostics: {
          ...this.browserDiagnostics(),
          boundary: options.phase || this.currentStageKey || 'unknown',
        },
      };
      return fetch(this.clientEventPath, {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      }).then((response) => response.ok).catch(() => false);
    }

    activateV2Fallback(legacy) {
      this.v3Active = false;
      this.hidden = true;
      this.closest('.product__preview')?.classList.remove('is-v3-active');
      this.closest('.theme-preview-popup')?.classList.remove('is-v3-active');
      if (
        typeof publish === 'function'
        && typeof PUB_SUB_EVENTS !== 'undefined'
        && PUB_SUB_EVENTS.filestackFileChoiced
      ) {
        publish(PUB_SUB_EVENTS.filestackFileChoiced, { data: legacy });
      }
    }

    async uploadLegacyPhoto(file, options = {}) {
      try {
        const client = await this.waitForFilestack();
        const result = await client.upload(file);
        return this.rememberLegacyPhoto(result, file, options);
      } catch (error) {
        this.legacyPhoto = null;
        throw error;
      }
    }

    rememberLegacyPhoto(result, file, options = {}) {
      const legacy = {
        url: result.url,
        handle: result.handle,
        filename: result.filename || file.name,
        mimetype: result.mimetype || file.type,
        size: result.size || file.size,
        source: result.source || 'local_file_system',
        uploadId: result.uploadId || result.handle,
        accountReorderHydration: options.preserveReorderSource === true,
      };
      this.legacyPhoto = legacy;
      if (!options.preserveReorderSource) this.markAccountReorderPhotoChanged();
      this.setLineProperty('Photo', legacy.url, false);
      this.setLineProperty('_preview_image_url', this.posterFullUrl || legacy.url, true);
      this.setLineProperty(
        '_preview_image_thumbnail_url',
        this.posterThumbnailUrl || legacy.url,
        true,
      );
      this.emitPreviewState(this.dataset.state === 'ready' ? 'ready' : 'creating');
      this.persistHandoff();
      return legacy;
    }

    markAccountReorderPhotoChanged() {
      const source = this.productForm?.querySelector(
        '[name="properties[_reorder_source_job_id]"]'
      );
      if (!source?.value) return;
      this.setLineProperty('_reorder_photo_changed', 'true', true);
    }

    async waitForFilestack() {
      for (let attempt = 0; attempt < 80; attempt += 1) {
        if (window.filestackInstance?.client?.upload) return window.filestackInstance.client;
        await wait(100);
      }
      throw new Error('Legacy photo storage is unavailable.');
    }

    setLineProperty(name, value, privateProperty) {
      const form = this.productForm || (
        this.dataset.productFormId ? document.getElementById(this.dataset.productFormId) : null
      );
      if (!form || !value) return;
      const propertyName = `properties[${name}]`;
      let input = form.querySelector(`[name="${propertyName}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = propertyName;
        if (privateProperty) input.dataset.livePreviewV3 = 'true';
        form.append(input);
      }
      input.value = value;
    }

    clearLineProperties() {
      const form = this.productForm;
      if (!form) return;
      [
        'properties[Photo]',
        'properties[_preview_image_url]',
        'properties[_preview_image_thumbnail_url]',
        'properties[_live_preview_v3_session_id]',
      ].forEach((name) => form.querySelector(`[name="${name}"]`)?.remove());
    }

    async ensureCurrentDesignReady() {
      if (this.createPromise) await this.createPromise;
      const deadline = Date.now() + 120000;

      while (Date.now() < deadline) {
        this.syncSelectionFromControls();
        const desiredSignature = this.selectionSignature(this.desiredSelection);
        if (
          desiredSignature !== this.lastSentSignature
          && !this.configureInFlight
          && (this.inferenceStatus === 'ready' || this.statusValue === 'ready' || this.statusValue === 'compiling')
        ) {
          window.clearTimeout(this.configureTimer);
          await this.configure();
        }

        if (
          this.statusValue === 'ready'
          && !this.configureInFlight
          && desiredSignature === this.lastSentSignature
        ) {
          if (this.posterFullUrl && this.posterUploadRevision === this.currentRevision) return;
          this.schedulePosterCapture(0);
        }

        if (this.statusPath && this.previewToken && !this.pollInFlight) {
          const payload = await this.requestJson(this.statusPath, {
            method: 'POST',
            body: JSON.stringify({ preview_token: this.previewToken }),
            diagnosticBoundary: 'cart_submit_status',
          });
          this.applyResponse(payload);
        }
        await wait(500);
      }

      throw new Error(this.dataset.copyCartError);
    }

    async handleFormSubmit(event) {
      if (!this.v3Active || this.allowNextSubmit) {
        this.allowNextSubmit = false;
        return;
      }

      if (this.submissionInFlight) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      if (!this.file) {
        if (this.cartEditContext) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showError(this.dataset.copyCartEditError);
        }
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      const submitter = event.submitter;
      this.submissionInFlight = true;
      this.setSubmissionUi(true);
      try {
        if (!this.sessionId || !this.cartAssociationPath) await this.createPromise;
        if (!this.sessionId || !this.cartAssociationPath || !this.previewToken) {
          throw new Error(this.dataset.copyCartError);
        }

        await this.ensureCurrentDesignReady();

        const cartResponse = await fetch(`${window.routes?.root || '/'}cart.js`, {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        if (!cartResponse.ok) throw new Error(this.dataset.copyCartError);
        const cart = await cartResponse.json();
        if (!cart.token) throw new Error(this.dataset.copyCartError);

        if (this.associatedCartToken !== cart.token) {
          const payload = await this.requestJson(this.cartAssociationPath, {
            method: 'POST',
            diagnosticBoundary: 'cart_association',
            body: JSON.stringify({
              preview_token: this.previewToken,
              cart_token: cart.token,
            }),
          });
          this.applyResponse(payload);
          this.associatedCartToken = cart.token;
        }

        this.setLineProperty('_live_preview_v3_session_id', this.sessionId, true);
        this.emitAnalytics('live_preview_v3_add_to_cart');
        this.allowNextSubmit = true;
        if (submitter && this.productForm.requestSubmit) this.productForm.requestSubmit(submitter);
        else this.productForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      } catch (error) {
        this.allowNextSubmit = false;
        this.releaseSubmissionUi();
        this.showError(this.dataset.copyCartError);
      }
    }

    setSubmissionUi(isLoading) {
      const productFormHost = this.productForm?.closest('product-form');
      const label = this.isCartEditActive() ? 'Updating crystal…' : 'Adding…';
      productFormHost?.setSubmissionLoading?.(isLoading, label, { lock: false });
    }

    releaseSubmissionUi() {
      this.submissionInFlight = false;
      this.productForm?.closest('product-form')?.setSubmissionLoading?.(false);
    }

    async handleHomepageNavigation(event) {
      if (!this.v3Active || !this.file) return;
      const href = this.homeProductLink?.getAttribute('href');
      if (!href || href === '#') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      try {
        if (!this.sessionId || !this.previewToken) await this.createPromise;
        if (!this.sessionId || !this.previewToken) {
          throw new Error(this.dataset.copyErrorBody);
        }
        this.persistHandoff();
        window.location.assign(href);
      } catch (error) {
        this.handlePreviewFailure(error, { phase: 'homepage_handoff' });
      }
    }

    emitPreviewState(state) {
      const detail = {
        formId: this.dataset.productFormId,
        state,
        imageUrl: this.posterFullUrl || this.legacyPhoto?.url || this.objectUrl,
        thumbnailUrl: this.posterThumbnailUrl || this.legacyPhoto?.url || this.objectUrl,
        provider: 'v3',
        stageKey: this.currentStageKey,
        stageIndex: this.currentStageIndex,
        stageCount: 4,
        stageLabel: this.currentStageLabel,
        generationKind: this.generationKind,
      };
      const signature = JSON.stringify([
        state,
        detail.imageUrl,
        detail.thumbnailUrl,
        detail.stageKey,
        detail.stageIndex,
        detail.stageLabel,
        detail.generationKind,
      ]);
      if (signature === this.lastPreviewStateSignature) return;
      this.lastPreviewStateSignature = signature;
      document.dispatchEvent(new CustomEvent('ccm:preview-state', {
        detail,
      }));
    }

    emitAnalytics(eventName) {
      const lifecycleEvents = new Set([
        'live_preview_v3_upload_started',
        'live_preview_v3_session_created',
        'live_preview_v3_ready',
        'live_preview_v3_failed',
      ]);
      if (lifecycleEvents.has(eventName)) {
        if (this.analyticsEmitted.has(eventName)) return;
        this.analyticsEmitted.add(eventName);
      }
      const selection = this.desiredSelection || {};
      const variantId = this.productForm?.querySelector('[name="id"]')?.value || '';
      const payload = {
        event: eventName,
        live_preview_store: this.dataset.store || 'ccm',
        live_preview_version: 'v3',
        storefront_epoch: 'ccm-live-preview-v3-20260803',
        variant_id: variantId,
        live_preview_crystal_sku: selection.crystal_sku || '',
        live_preview_base_sku: selection.base_sku || 'none',
        live_preview_text_present: Boolean(selection.engraving_text),
        live_preview_wall_time_ms: this.pollStartedAt ? Date.now() - this.pollStartedAt : 0,
        live_preview_session_id: this.sessionId || '',
      };
      if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
      const crystalPricingEvent = {
        live_preview_v3_upload_started: 'crystal_preview_started',
        live_preview_v3_ready: 'crystal_preview_completed',
        live_preview_v3_failed: 'crystal_preview_failed',
      }[eventName];
      if (crystalPricingEvent && typeof window.Shopify?.analytics?.publish === 'function') {
        window.Shopify.analytics.publish(crystalPricingEvent, {
          previewVersion: 'v3',
          storefrontEpoch: payload.storefront_epoch,
          variantId,
          crystalSku: payload.live_preview_crystal_sku,
          baseSku: payload.live_preview_base_sku,
          textPresent: payload.live_preview_text_present,
          wallTimeMs: payload.live_preview_wall_time_ms,
        });
      }
      this.dispatchEvent(new CustomEvent(eventName, { detail: payload, bubbles: true }));
    }

    persistHandoff() {
      if (!this.sessionId || !this.previewToken) return;
      if (this.dataset.surface === 'pdp' && this.productForm) this.syncSelectionFromControls();
      const addOnVariantIds = Array.from(
        this.productForm?.querySelectorAll('input[data-addons-index][name$="[id]"]') || []
      ).map((input) => String(input.value || '')).filter(Boolean);
      const record = {
        version: 1,
        savedAt: Date.now(),
        sessionId: this.sessionId,
        previewToken: this.previewToken,
        statusPath: this.statusPath,
        configurePath: this.configurePath,
        cartAssociationPath: this.cartAssociationPath,
        rendererPath: this.rendererPath,
        posterPath: this.posterPath,
        clientEventPath: this.clientEventPath,
        posterFullUrl: this.posterFullUrl,
        posterThumbnailUrl: this.posterThumbnailUrl,
        status: this.statusValue,
        inferenceStatus: this.inferenceStatus,
        compilationRevision: this.currentRevision,
        selection: {
          crystal_sku: this.desiredSelection.crystal_sku,
          orientation: this.desiredSelection.orientation,
          base_sku: this.desiredSelection.base_sku,
          engraving_text: this.desiredSelection.engraving_text,
          engraving_font: this.desiredSelection.engraving_font,
        },
        sentSelectionSignature: this.lastSentSignature,
        legacyPhoto: this.legacyPhoto,
        addOnVariantIds,
        cartEditOriginalSessionId: this.cartEditContext?.sessionId || '',
        cartEditOriginalGroupId: this.cartEditContext?.groupId || '',
        cartEditOriginalLineKeys: Array.isArray(this.cartEditContext?.originalKeys)
          ? this.cartEditContext.originalKeys
          : [],
        cartEditProofRequested: Boolean(document.querySelector(
          `[data-proof-preview-toggle][data-proof-preview-form="${CSS.escape(this.dataset.productFormId || '')}"]`
        )?.checked),
        cartEditOrientationAllowed: Boolean(document.querySelector(
          `[name="properties[Orientation]"][form="${CSS.escape(this.dataset.productFormId || '')}"]`
        )?.checked),
      };
      try {
        sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(record));
      } catch (_error) {
        // The same-page preview remains usable when session storage is blocked.
      }
    }

    cartEditSessionId() {
      const value = new URLSearchParams(window.location.search).get('edit_preview_session') || '';
      return /^[a-f0-9-]{36}$/i.test(value) ? value.toLowerCase() : '';
    }

    cartEditTarget() {
      const parameters = new URLSearchParams(window.location.search);
      return {
        groupId: parameters.get('edit_cart_group') || '',
        lineKey: parameters.get('edit_cart_key') || '',
      };
    }

    cartItemProperties(item) {
      return item?.properties && typeof item.properties === 'object' ? item.properties : {};
    }

    async waitForCartEditControls() {
      if (document.readyState === 'loading') {
        await new Promise((resolve) => document.addEventListener('DOMContentLoaded', resolve, { once: true }));
      }
      await wait(350);
    }

    setCartEditControlValue(name, value) {
      const selector = `[name="${CSS.escape(name)}"]`;
      const controls = Array.from(document.querySelectorAll(selector)).filter((control) => {
        const formId = control.getAttribute('form');
        return !formId || formId === this.dataset.productFormId || this.productForm?.contains(control);
      });
      controls.forEach((control) => {
        if (!('value' in control)) return;
        control.value = value == null ? '' : String(value);
        control.dispatchEvent(new Event('input', { bubbles: true }));
        control.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    hydrateCartEditControls(parentItem, groupItems, handoffRecord = null) {
      const properties = this.cartItemProperties(parentItem);
      if (!handoffRecord) {
        Object.entries(properties).forEach(([property, value]) => {
          if (!property.startsWith('_') && property !== 'Photo' && property !== 'Crystal SKU') {
            this.setCartEditControlValue(`properties[${property}]`, value);
          }
        });
      } else {
        this.setCartEditControlValue(
          'properties[Engrave Text]',
          handoffRecord.selection?.engraving_text || '',
        );
        this.setCartEditControlValue(
          'properties[Font]',
          handoffRecord.selection?.engraving_font || 'Georgia',
        );
      }

      const quantity = this.productForm?.querySelector('[name="quantity"]');
      if (quantity && parentItem.quantity) quantity.value = String(parentItem.quantity);

      const proofToggle = document.querySelector(
        `[data-proof-preview-toggle][data-proof-preview-form="${CSS.escape(this.dataset.productFormId)}"]`
      );
      if (proofToggle) {
        proofToggle.checked = handoffRecord
          ? Boolean(handoffRecord.cartEditProofRequested)
          : properties['Request 3D Proof'] === 'Yes' || properties._customer_proof_requested === '1';
      }

      const orientationToggle = document.querySelector(
        `[name="properties[Orientation]"][form="${CSS.escape(this.dataset.productFormId)}"]`
      );
      if (orientationToggle) {
        orientationToggle.checked = handoffRecord
          && typeof handoffRecord.cartEditOrientationAllowed === 'boolean'
          ? handoffRecord.cartEditOrientationAllowed
          : Boolean(properties.Orientation);
        orientationToggle.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const groupedVariantIds = new Set(groupItems.map((item) => String(item.variant_id || item.id || '')));
      const scope = this.closest('product-info') || document;
      const baseInput = Array.from(scope.querySelectorAll('variant-selects-extended input[data-variant-id]'))
        .find((input) => (
          handoffRecord?.selection?.base_sku
            ? String(input.dataset.baseSku || '') === String(handoffRecord.selection.base_sku)
            : groupedVariantIds.has(String(input.dataset.variantId || input.value || ''))
        ));
      if (baseInput && !baseInput.checked) baseInput.click();

      const addOnVariantIds = handoffRecord
        ? new Set((handoffRecord.addOnVariantIds || []).map(String))
        : groupedVariantIds;
      scope.querySelectorAll('.additional-row button[data-variant-id]').forEach((button) => {
        const variantId = String(button.dataset.variantId || '');
        if (!variantId || !addOnVariantIds.has(variantId)) return;
        const alreadySelected = this.productForm?.querySelector(
          `input[data-addons-index][value="${CSS.escape(variantId)}"]`
        );
        if (!alreadySelected) button.click();
      });

      this.productForm?.closest('product-form')?.syncCustomerProofPreviewProperties?.();
      this.desiredSelection = this.readSelection();
    }

    setSubmitButtonLabel(button, label) {
      const directSpan = button.querySelector(':scope > span');
      if (directSpan) {
        directSpan.textContent = label;
        return;
      }
      const textNode = Array.from(button.childNodes)
        .find((node) => node.nodeType === Node.TEXT_NODE && node.nodeValue.trim());
      if (textNode) {
        textNode.nodeValue = `\n      ${label}\n    `;
        return;
      }
      button.prepend(document.createTextNode(label));
    }

    activateCartEditMode() {
      const productInfo = this.closest('product-info');
      productInfo?.classList.add('is-v3-cart-edit');
      if (this.cartEditBanner) this.cartEditBanner.hidden = false;

      const formId = this.dataset.productFormId;
      document.querySelectorAll(
        `#${CSS.escape(formId)} button[type="submit"], button[type="submit"][form="${CSS.escape(formId)}"]`
      ).forEach((button) => this.setSubmitButtonLabel(button, 'Update crystal'));

      const productFormHost = this.productForm?.closest('product-form');
      if (productFormHost) productFormHost.cartEditCoordinator = this;
    }

    async restoreCartEdit(sessionId) {
      this.restoringCartEdit = true;
      try {
        const editTarget = this.cartEditTarget();
        this.cartEditContext = { sessionId, groupId: editTarget.groupId, originalKeys: [] };
        this.activateCartEditMode();
        this.currentStageKey = 'intake';
        this.currentStageIndex = 1;
        this.currentStageLabel = this.stageLabels.intake || this.dataset.copyProcessingHeading;
        this.pollStartedAt = Date.now();
        this.showWorking(
          this.dataset.copyUpdatingHeading,
          this.dataset.copyProcessingBody,
          1,
          'update',
        );

        const cartResponse = await fetch(`${window.routes?.root || '/'}cart.js`, {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        if (!cartResponse.ok) throw new Error(this.dataset.copyCartEditError);
        const cart = await cartResponse.json();
        const parentItem = cart.items?.find((item) => {
          const properties = this.cartItemProperties(item);
          if (properties._live_preview_v3_session_id !== sessionId) return false;
          if (editTarget.groupId && properties['_Group Identify'] !== editTarget.groupId) return false;
          if (editTarget.lineKey && item.key !== editTarget.lineKey) return false;
          return true;
        });
        if (!parentItem || !cart.token) throw new Error(this.dataset.copyCartEditError);

        const parentProperties = this.cartItemProperties(parentItem);
        const groupId = parentProperties['_Group Identify'] || '';
        const groupItems = groupId
          ? cart.items.filter((item) => this.cartItemProperties(item)['_Group Identify'] === groupId)
          : [parentItem];
        const groupedV3Parents = groupItems.filter((item) => (
          Boolean(this.cartItemProperties(item)._live_preview_v3_session_id)
        ));
        if (groupedV3Parents.length > 1) {
          throw new Error('These customized items are combined in one cart group. Remove the extra copy before editing this crystal.');
        }

        const editHandoff = this.readHandoffRecord();
        const canUseEditHandoff = this.validHandoffRecord(editHandoff)
          && editHandoff.cartEditOriginalSessionId === sessionId
          && (!editTarget.groupId || editHandoff.cartEditOriginalGroupId === editTarget.groupId)
          && (
            !editTarget.lineKey
            || editHandoff.cartEditOriginalLineKeys?.includes(editTarget.lineKey)
          );

        await this.waitForCartEditControls();
        this.hydrateCartEditControls(
          parentItem,
          groupItems,
          canUseEditHandoff ? editHandoff : null,
        );

        if (canUseEditHandoff) {
          this.applyHandoffRecord(editHandoff);
          this.associatedCartToken = editHandoff.sessionId === sessionId ? cart.token : '';
        } else {
          const createUrl = new URL(this.dataset.createPath, window.location.origin);
          const resumePath = `${createUrl.pathname.replace(/\/$/, '')}/${encodeURIComponent(sessionId)}/resume?preview_v3=1`;
          const payload = await this.requestJson(resumePath, {
            method: 'POST',
            body: JSON.stringify({ cart_token: cart.token }),
            diagnosticBoundary: 'cart_resume',
          });

          this.sessionId = sessionId;
          this.file = { restored: true };
          this.legacyPhoto = parentProperties.Photo
            ? { url: parentProperties.Photo, filename: 'customer-photo' }
            : null;
          this.legacyUploadPromise = Promise.resolve(this.legacyPhoto);
          this.objectUrl = this.legacyPhoto?.url || parentProperties._preview_image_thumbnail_url || '';
          this.lastSentSignature = this.selectionSignature({
            ...this.desiredSelection,
            crystal_sku: payload.crystal_sku || parentProperties['Crystal SKU'] || '',
            base_sku: payload.base_sku || this.desiredSelection.base_sku,
            engraving_text: parentProperties['Engrave Text'] || parentProperties['Engrave text'] || '',
            engraving_font: parentProperties.Font || 'Georgia',
          });
          this.applyResponse(payload);
          this.associatedCartToken = cart.token;
        }
        this.cartEditContext = {
          sessionId,
          groupId,
          originalKeys: groupItems.map((item) => item.key).filter(Boolean),
        };

        if (!this.legacyPhoto && parentProperties.Photo) {
          this.legacyPhoto = { url: parentProperties.Photo, filename: 'customer-photo' };
          this.legacyUploadPromise = Promise.resolve(this.legacyPhoto);
        }
        if (this.legacyPhoto?.url) this.setLineProperty('Photo', this.legacyPhoto.url, false);
        this.setLineProperty('_live_preview_v3_session_id', this.sessionId, true);
        this.activateCartEditMode();
        this.persistHandoff();

        if (new URLSearchParams(window.location.search).get('edit_focus') === 'text') {
          const textControl = document.querySelector(
            `[name="properties[Engrave Text]"][form="${CSS.escape(this.dataset.productFormId)}"]`
          );
          textControl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          window.setTimeout(() => textControl?.focus({ preventScroll: true }), 450);
        }
      } catch (error) {
        this.showError(error?.message || this.dataset.copyCartEditError);
        throw error;
      } finally {
        this.restoringCartEdit = false;
      }
    }

    isCartEditActive() {
      return Boolean(this.cartEditContext?.sessionId);
    }

    async completeCartReplacement(addResponse) {
      if (!this.isCartEditActive()) return false;
      const addedItems = Array.isArray(addResponse?.items) ? addResponse.items : [addResponse];
      const replacementParent = addedItems.find((item) => (
        this.cartItemProperties(item)._live_preview_v3_session_id === this.sessionId
      ));
      if (!replacementParent) {
        throw new Error('The updated crystal was not added. Your original cart item is unchanged.');
      }

      const replacementGroupId = this.cartItemProperties(replacementParent)['_Group Identify'] || '';
      if (replacementGroupId && replacementGroupId === this.cartEditContext.groupId) {
        throw new Error('The updated crystal could not be separated from the original cart item.');
      }

      const cartResponse = await fetch(`${window.routes?.root || '/'}cart.js`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) throw new Error('Your updated crystal was added, but the original could not be removed.');
      const cart = await cartResponse.json();
      const replacementExists = cart.items?.some((item) => {
        const properties = this.cartItemProperties(item);
        return properties._live_preview_v3_session_id === this.sessionId
          && (!replacementGroupId || properties['_Group Identify'] === replacementGroupId);
      });
      if (!replacementExists) {
        throw new Error('The updated crystal could not be verified. Your original cart item is unchanged.');
      }

      const originalKeys = new Set(this.cartEditContext.originalKeys || []);
      const originals = cart.items.filter((item) => {
        const properties = this.cartItemProperties(item);
        if (replacementGroupId && properties['_Group Identify'] === replacementGroupId) return false;
        if (this.cartEditContext.groupId) {
          return properties['_Group Identify'] === this.cartEditContext.groupId;
        }
        return originalKeys.has(item.key);
      });
      if (!originals.length) {
        throw new Error('Your updated crystal was added, but the original cart item could not be found.');
      }

      const updates = Object.fromEntries(originals.map((item) => [item.key, 0]));
      const updateResponse = await fetch(`${window.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!updateResponse.ok) {
        throw new Error('Your updated crystal was added, but the original could not be removed.');
      }

      try {
        sessionStorage.removeItem(HANDOFF_KEY);
      } catch (_error) {
        // Navigation still completes when session storage is unavailable.
      }
      window.location.assign(`${window.routes?.root || '/'}cart`);
      return true;
    }

    readHandoffRecord() {
      try {
        return safeJsonParse(sessionStorage.getItem(HANDOFF_KEY));
      } catch (_error) {
        return null;
      }
    }

    validHandoffRecord(record) {
      return Boolean(
        record
        && record.version === 1
        && record.sessionId
        && record.previewToken
        && Date.now() - Number(record.savedAt || 0) <= HANDOFF_MAX_AGE_MS
      );
    }

    restoreHandoff() {
      const record = this.readHandoffRecord();
      if (!this.validHandoffRecord(record)) return;
      this.applyHandoffRecord(record);
    }

    restoreHandoffControls(selection = {}) {
      if (this.dataset.surface !== 'pdp') return;
      const scope = this.closest('product-info') || document;
      const baseSku = String(selection.base_sku || 'none');
      const baseInput = Array.from(
        scope.querySelectorAll('variant-selects-extended input[data-base-sku]')
      ).find((input) => String(input.dataset.baseSku || 'none') === baseSku);
      if (baseInput && !baseInput.checked) {
        baseInput.checked = true;
        baseInput.closest('variant-selects-extended')?.updateProductForm?.();
      }

      const setControlValue = (name, value) => {
        const formId = this.dataset.productFormId;
        const control = formId
          ? document.querySelector(
            `[form="${CSS.escape(formId)}"][name="${CSS.escape(name)}"]`
          ) || this.productForm?.querySelector(`[name="${CSS.escape(name)}"]`)
          : this.productForm?.querySelector(`[name="${CSS.escape(name)}"]`);
        if (control && 'value' in control) control.value = value == null ? '' : String(value);
      };
      setControlValue('properties[Engrave Text]', selection.engraving_text || '');
      setControlValue('properties[Font]', selection.engraving_font || 'Georgia');
    }

    applyHandoffRecord(record) {
      this.sessionId = record.sessionId;
      this.previewToken = record.previewToken;
      this.statusPath = record.statusPath || '';
      this.configurePath = record.configurePath || '';
      this.cartAssociationPath = record.cartAssociationPath || '';
      this.rendererPath = record.rendererPath || '';
      this.posterPath = record.posterPath || '';
      this.clientEventPath = record.clientEventPath || '';
      this.posterFullUrl = record.posterFullUrl || '';
      this.posterThumbnailUrl = record.posterThumbnailUrl || '';
      this.statusValue = record.status || '';
      this.inferenceStatus = record.inferenceStatus || '';
      this.currentRevision = Number(record.compilationRevision ?? -1);
      this.posterUploadRevision = this.posterFullUrl && this.currentRevision >= 0
        ? this.currentRevision
        : -1;
      this.posterUploadingRevision = -1;
      this.legacyPhoto = record.legacyPhoto || null;
      this.file = { restored: true };
      this.legacyUploadPromise = Promise.resolve(this.legacyPhoto);
      if (this.legacyPhoto?.url) {
        this.objectUrl = this.legacyPhoto.url;
        this.setLineProperty('Photo', this.legacyPhoto.url, false);
        this.setLineProperty(
          '_preview_image_url',
          this.posterFullUrl || this.legacyPhoto.url,
          true,
        );
        this.setLineProperty(
          '_preview_image_thumbnail_url',
          this.posterThumbnailUrl || this.legacyPhoto.url,
          true,
        );
      }

      this.restoreHandoffControls(record.selection || {});
      const currentSelection = this.readSelection();
      const restoredSelection = {
        ...this.desiredSelection,
        ...(record.selection || {}),
        ...currentSelection,
      };
      this.desiredSelection = restoredSelection;
      // The handoff selection describes the page we left, while currentSelection
      // describes the PDP we just opened. Keep the signature that the backend
      // session actually rendered so a product-page change is configured before
      // the restored renderer is shown. Older records predate the explicit sent
      // signature, so their saved selection is the safest compatible fallback.
      this.lastSentSignature = record.sentSelectionSignature
        || this.selectionSignature(record.selection || {});
      const currentSelectionSignature = this.selectionSignature(currentSelection);
      this.generationKind = 'initial';
      this.currentStageIndex = 1;
      this.currentStageKey = 'intake';
      this.currentStageLabel = this.stageLabels.intake || this.dataset.copyProcessingHeading;
      this.showWorking(
        this.currentStageLabel,
        this.dataset.copyProcessingBody,
        1,
        this.generationKind,
      );
      this.emitPreviewState('creating');
      this.pollStartedAt = Date.now();
      if (currentSelectionSignature !== this.lastSentSignature) this.scheduleConfigure(0);
      if (this.statusPath) {
        this.statusValue = '';
        this.schedulePoll();
      }
    }

    focusViewer() {
      this.setAttribute('tabindex', '-1');
      this.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center',
      });
      this.focus({ preventScroll: true });
    }
  }

  customElements.define('ccm-live-preview-v3', CcmLivePreviewV3);
})();
